<script setup lang="ts">
import FaqItem from './FaqItem.vue';

export type ItemType = {
    title: string;
    items: { title?: string; value?: string; noSchema?: boolean }[];
};

interface IProps {
    items?: ItemType[];
    limit?: number;
    compactTitle?: boolean;
    noSchema?: boolean;
    highlight?: string;
}
const { items = [], limit, compactTitle, noSchema } = defineProps<IProps>();
if (!noSchema && items.length) {
    useSchemaOrg([
        defineWebPage({
            '@type': 'FAQPage',
            '@context': 'https://schema.org',
        }),
        ...(items
            ?.flatMap(i => i.items)
            .filter(i => !i.noSchema)
            .map(i =>
                defineQuestion({
                    '@type': 'Question',
                    name: i.title,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: i.value ?? '',
                    },
                })
            ) || []),
    ]);
}
const activeIndex = ref<string>();
const expand = ref(false);
const limitValue = ref(0);
const allItems = computed<ItemType[]>(() => {
    let count = 0;
    if (!limitValue.value) {
        return items;
    }
    return (
        items?.reduce((acc, item) => {
            if (limitValue.value - count > 0) {
                acc.push({
                    ...item,
                    items: item.items.slice(0, limitValue.value - count),
                });
                count += item.items.length;
            }
            return acc;
        }, [] as ItemType[]) || []
    );
});
const toggle = (id: string) => {
    activeIndex.value = activeIndex.value === id ? undefined : id;
};
const more = () => {
    expand.value = true;
    limitValue.value = 10000;
};
onBeforeMount(() => {
    limitValue.value = limit ?? 0;
});
const i18n = useI18n();
</script>

<template>
    <div style="width: 100%">
        <div v-for="(item, i) in allItems" :key="`group-${i}`">
            <div v-if="item.title" class="group">
                <span class="line" />
                <b :class="{ compact: compactTitle }">{{ item.title }}</b>
                <span class="line" />
            </div>
            <FaqItem
                v-for="(faq, j) in item.items"
                :key="j"
                :expanded="activeIndex === `accordion${i}-${j}`"
                :faq
                :highlight
                @toggle="toggle(`accordion${i}-${j}`)"
            >
                <template #separator>
                    <div v-show="j !== item.items.length - 1" class="line" />
                </template>
            </FaqItem>
        </div>
        <mr-button
            v-if="limitValue && !expand"
            class="more-btn"
            variant="bordered"
            @click="more"
        >
            {{ i18n.t('Faq.more') }}
        </mr-button>
    </div>
</template>

<style scoped lang="scss">
.group b {
    text-align: right;
    display: block;
    margin-inline-start: 0;
    margin-inline-end: 0;
    margin-top: 1.33em;
    margin-bottom: 1.33em;
    &.compact {
        font-style: normal;
        @include font-weight(500);
        font-size: 20px;
        line-height: 31px;
        text-align: right;
        color: usePalette($p-cool-gray, $p-800);
    }
}

.line {
    width: 100%;
    height: 1px;
    background-color: usePalette($p-cool-gray, $p-100);
}

.more-btn {
    width: 150px !important;
    height: 48px !important;
    margin-top: 22px;
    float: left;
}

@media (max-width: $app-mobile-max-width) {
    .more-btn {
        width: 100% !important;
        float: none;
    }
}
</style>
